/**
 * ProjectRax — Offline Sync Manager
 * Queues API requests when offline and replays them when back online.
 * Uses IndexedDB for persistent storage across browser sessions.
 *
 * Usage:
 *   import { offlineQueue } from './offlineSync';
 *
 *   // Instead of direct API call:
 *   offlineQueue.add({
 *     method: 'POST',
 *     url: '/api/v1/projects/xxx/attendance',
 *     data: { date: '2026-02-13', entries: [...] },
 *     label: 'Attendance — 13 Feb',
 *   });
 */

import { getAccessToken } from '../services/api';

const DB_NAME = 'projectrax_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_requests';

// ─── IndexedDB Helpers ───────────────────────────────
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function addToStore(data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.add({
      ...data,
      status: 'pending',
      createdAt: new Date().toISOString(),
      attempts: 0,
    });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getAllPending() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll('pending');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function updateStatus(id, status, error = null) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const item = getReq.result;
      if (!item) return resolve();
      item.status = status;
      item.attempts = (item.attempts || 0) + 1;
      item.lastAttempt = new Date().toISOString();
      if (error) item.lastError = error;
      if (status === 'synced') item.syncedAt = new Date().toISOString();
      store.put(item);
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

async function deleteItem(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getCount() {
  const items = await getAllPending();
  return items.length;
}

async function clearSynced() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.openCursor('synced');
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Sync Engine ─────────────────────────────────────
let isSyncing = false;
let syncListeners = [];

function onSyncUpdate(callback) {
  syncListeners.push(callback);
  return () => { syncListeners = syncListeners.filter((c) => c !== callback); };
}

function notifyListeners(event) {
  syncListeners.forEach((cb) => {
    try { cb(event); } catch (e) { console.error('[OfflineSync] Listener error:', e); }
  });
}

async function syncAll() {
  if (isSyncing) return;
  if (!navigator.onLine) return;

  isSyncing = true;
  notifyListeners({ type: 'sync_start' });

  try {
    const pending = await getAllPending();
    let synced = 0, failed = 0;

    for (const item of pending) {
      if (item.attempts >= 3) {
        await updateStatus(item.id, 'failed', 'Max retries exceeded');
        failed++;
        continue;
      }

      try {
        const token = getAccessToken();
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

        const response = await fetch(`${baseUrl}${item.url}`, {
          method: item.method || 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: item.data ? JSON.stringify(item.data) : undefined,
        });

        if (response.ok) {
          await updateStatus(item.id, 'synced');
          synced++;
        } else if (response.status === 409) {
          // Conflict (e.g., attendance already exists) — mark as synced
          await updateStatus(item.id, 'synced');
          synced++;
        } else {
          const err = await response.json().catch(() => ({}));
          await updateStatus(item.id, 'pending', err.message || `HTTP ${response.status}`);
          failed++;
        }
      } catch (err) {
        await updateStatus(item.id, 'pending', err.message);
        failed++;
      }
    }

    // Clean up synced items
    await clearSynced();

    notifyListeners({ type: 'sync_complete', synced, failed, remaining: await getCount() });
  } catch (err) {
    console.error('[OfflineSync] Sync error:', err);
    notifyListeners({ type: 'sync_error', error: err.message });
  } finally {
    isSyncing = false;
  }
}

// ─── Auto-sync on reconnect ─────────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('[OfflineSync] Back online — syncing...');
    notifyListeners({ type: 'online' });
    setTimeout(syncAll, 1000); // Wait 1s for stable connection
  });

  window.addEventListener('offline', () => {
    console.log('[OfflineSync] Went offline');
    notifyListeners({ type: 'offline' });
  });

  // Register service worker for background sync
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      if ('sync' in registration) {
        // Trigger background sync when requests are queued
        registration.sync.register('projectrax-sync').catch(() => {});
      }
    }).catch(() => {});
  }
}

// ─── Public API ──────────────────────────────────────
export const offlineQueue = {
  /** Add a request to the offline queue */
  add: addToStore,
  /** Get all pending requests */
  getPending: getAllPending,
  /** Get count of pending requests */
  getCount,
  /** Manually trigger sync */
  sync: syncAll,
  /** Delete a queued item */
  delete: deleteItem,
  /** Listen for sync events */
  onUpdate: onSyncUpdate,
  /** Check if currently online */
  isOnline: () => navigator.onLine,
};

export default offlineQueue;
